import { isPresent } from './helpers';

/**
 * Updates the URL without causing a page refresh
 * @param path - The new path to navigate to
 * @param options - Optional configuration
 */
export function updateUrlWithoutRefresh(path: string, options?: { replace?: boolean }) {
  if (typeof window === 'undefined') return;

  const { replace = true } = options || {};

  if (replace) {
    window.history.replaceState(null, '', path);
  } else {
    window.history.pushState(null, '', path);
  }
}

/**
 * Updates the URL query parameters without causing a page refresh
 * @param params - Object containing query parameters to update
 * @param options - Optional configuration
 */
type PrimitiveSearchParam = string | number | boolean;

export function updateUrlParamsWithoutRefresh(
  params: Record<string, PrimitiveSearchParam | PrimitiveSearchParam[] | undefined>,
  options?: { replace?: boolean },
) {
  if (typeof window === 'undefined') return;

  const { replace = true } = options || {};
  const url = new URL(window.location.href);

  // Update or add query parameters
  Object.entries(params).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      url.searchParams.delete(key);
      if (value.length === 0) {
        return;
      }
      value.forEach((entry) => {
        url.searchParams.append(key, String(entry));
      });
      return;
    }

    if (value !== undefined) {
      url.searchParams.set(key, String(value));
    } else {
      url.searchParams.delete(key);
    }
  });

  const newUrl = url.pathname + url.search + url.hash;

  if (replace) {
    window.history.replaceState(null, '', newUrl);
  } else {
    window.history.pushState(null, '', newUrl);
  }
}

export type SearchParamValue = string | number | boolean | undefined | null;

export type UrlParamsOptions = {
  path?: Array<string | number>;
  query?: Record<string, SearchParamValue | SearchParamValue[]>;
};

export type RequestPathOptions = {
  path: string;
  params?: Array<string | number>;
  search?: Record<string, SearchParamValue | SearchParamValue[]>;
};

function formatQueryParamValue(key: string, value: SearchParamValue) {
  if (value === null || value === undefined || value === '') return;

  return `${key}=${encodeURIComponent(value.toString())}`;
}

export function formatUrl({ params = [], search = {}, ...options }: RequestPathOptions) {
  const base = options.path.endsWith('/') ? options.path.slice(0, -1) : options.path;

  const encodedParams = params.filter(isPresent).map((value) => encodeURIComponent(value.toString()));

  const formattedParams = encodedParams.length > 0 ? `/${encodedParams.join('/')}` : '';

  const encodedSearchParams = Object.entries(search)
    .flatMap(([key, value]) =>
      Array.isArray(value) ? value.map((v) => formatQueryParamValue(key, v)) : formatQueryParamValue(key, value),
    )
    .filter(isPresent);

  const formattedSearchParams = encodedSearchParams.length > 0 ? `?${encodedSearchParams.join('&')}` : '';

  return `${base}${formattedParams}${formattedSearchParams}`;
}

export function createUrlPathWithParams(
  path: string,
  { path: pathParams = [], query: queryParams = {} }: UrlParamsOptions = {},
) {
  const formattedPathParams = pathParams
    .filter((value) => value !== undefined)
    .map((value) => encodeURIComponent(value.toString()));

  const formattedQueryParams = Object.entries(queryParams)
    .flatMap(([key, value]) =>
      Array.isArray(value) ? value.map((v) => formatQueryParamValue(key, v)) : formatQueryParamValue(key, value),
    )
    .filter(isPresent);

  const formattedPath = formattedPathParams.length > 0 ? `${path}/${formattedPathParams.join('/')}` : path;
  const formattedQuery = formattedQueryParams.length > 0 ? `?${formattedQueryParams.join('&')}` : '';

  return `${formattedPath}${formattedQuery}`;
}

export function reloadPage() {
  if (typeof window === 'undefined') return;

  window.location.reload();
}

/**
 * Strips the structural `/t/<tenantId>` prefix from a protected-route pathname,
 * returning the section base path used to key nav config and feature gating
 * (e.g. `ROUTES_CONFIG`, `route_access`).
 *
 * @param pathname - Full location pathname, e.g. `/t/acme/data-foundry/catalog`
 * @returns The section pathname, e.g. `/data-foundry/catalog`; `/` for the tenant root or empty input
 *
 * @example
 * getSectionPathname('/t/acme/data-foundry'); // '/data-foundry'
 * getSectionPathname('/t/acme');              // '/'
 * getSectionPathname('/usage');               // '/usage' (non-tenant paths pass through)
 */
export function getSectionPathname(pathname: string | null | undefined): string {
  return pathname?.replace(/^\/t\/[^/]+/, '') || '/';
}
